import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search, ArrowRight, Building, Calculator, GitCompareArrows, ShieldCheck, Briefcase, Scale, HomeIcon, Globe2, HeartHandshake, Flame, Tag, Star, TrendingUp, Compass } from "lucide-react";
import api from "@/lib/api";
import FeaturedCarousel from "@/components/FeaturedCarousel";
import { formatPrice } from "@/components/PropertyCard";

const CITIES = ["Lucknow", "Gurugram", "Noida", "Delhi", "Mumbai", "Bangalore", "Pune", "Hyderabad", "Dubai", "Singapore"];

export default function Home() {
  const [city, setCity] = useState("");
  const [type, setType] = useState("");
  const [search, setSearch] = useState("");
  const [featured, setFeatured] = useState([]);
  const [hotDeals, setHotDeals] = useState([]);
  const [stats, setStats] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/projects?featured=true&limit=6").then((r) => setFeatured(r.data || [])).catch(() => {});
    api.get("/projects?hot_deal=true&limit=4").then((r) => setHotDeals(r.data || [])).catch(() => {});
    api.get("/projects/cities/stats").then((r) => setStats(r.data || [])).catch(() => {});
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const params = new URLSearchParams();
    if (city) params.set("city", city);
    if (type) params.set("type", type);
    if (search) params.set("q", search);
    navigate(`/projects?${params.toString()}`);
  };

  return (
    <div data-testid="home-page">
      {/* Hero — bright cream gradient with a search panel */}
      <section className="relative overflow-hidden">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-gradient-to-br from-amber-50 via-white to-amber-50/30" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,_rgba(212,164,55,0.15)_0%,_transparent_50%)]" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_left,_rgba(37,99,235,0.06)_0%,_transparent_50%)]" />

        {/* Decorative photo strip on the right */}
        <div className="absolute right-0 top-0 h-full w-1/2 hidden lg:block">
          <div className="grid grid-cols-2 gap-4 p-8 h-full">
            <div className="space-y-4 mt-12">
              <div className="rounded-2xl overflow-hidden h-48 shadow-xl">
                <img src="https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=800" alt="" className="w-full h-full object-cover" />
              </div>
              <div className="rounded-2xl overflow-hidden h-64 shadow-xl">
                <img src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?w=800" alt="" className="w-full h-full object-cover" />
              </div>
            </div>
            <div className="space-y-4">
              <div className="rounded-2xl overflow-hidden h-64 shadow-xl">
                <img src="https://images.unsplash.com/photo-1582268611958-ebfd161df9d8?w=800" alt="" className="w-full h-full object-cover" />
              </div>
              <div className="rounded-2xl overflow-hidden h-48 shadow-xl">
                <img src="https://images.unsplash.com/photo-1564013799919-ab600027ffc6?w=800" alt="" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>
        </div>

        <div className="relative max-w-7xl mx-auto w-full px-4 md:px-8 pt-16 lg:pt-20 pb-12 z-10">
          <div className="max-w-2xl fade-up">
            <div className="inline-flex items-center gap-2 bg-white border border-amber-200 rounded-full px-4 py-1.5 mb-6 shadow-sm">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
              <span className="text-xs font-semibold text-slate-700">Since 2015 · UP RERA Agent UPRERAAGT15084</span>
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-slate-900 leading-[1.05] tracking-tight font-bold">
              Find your <span className="text-[#D4A437] relative inline-block">
                dream home
                <span className="absolute bottom-1 left-0 right-0 h-1.5 bg-amber-200/60 -z-0"></span>
              </span><br />
              with the most trusted realty.
            </h1>
            <p className="text-slate-600 text-lg mt-6 max-w-2xl leading-relaxed">
              From Lucknow heritage neighbourhoods to Dubai waterfronts and Singapore skyscrapers —
              one consultancy, every step of your ownership cycle.
            </p>
            <div className="mt-6 flex items-center gap-6 text-sm text-slate-600">
              <div className="flex items-center gap-1">
                {[1,2,3,4,5].map((i) => <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />)}
                <span className="ml-2 font-semibold">4.9 / 5</span>
              </div>
              <div className="flex items-center gap-2 text-slate-500">
                <ShieldCheck className="w-4 h-4 text-green-600" /> 3,000+ happy clients
              </div>
            </div>
          </div>

          <form
            onSubmit={handleSearch}
            data-testid="hero-search-form"
            className="bg-white shadow-[0_20px_60px_rgb(0,0,0,0.08)] rounded-2xl mt-12 p-3 grid md:grid-cols-12 gap-3 md:items-end relative z-10 max-w-5xl"
          >
            <div className="md:col-span-3">
              <label className="overline block mb-1.5 px-2">City</label>
              <select
                value={city}
                onChange={(e) => setCity(e.target.value)}
                data-testid="hero-city-select"
                className="w-full bg-white text-slate-900 px-3 py-3 text-sm font-medium focus:outline-none border-0 hover:bg-slate-50 rounded-lg cursor-pointer"
              >
                <option value="">All Cities</option>
                {CITIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div className="md:col-span-3 md:border-l md:border-slate-100">
              <label className="overline block mb-1.5 px-2 md:pl-4">Property Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value)}
                data-testid="hero-type-select"
                className="w-full bg-white text-slate-900 px-3 md:px-4 py-3 text-sm font-medium focus:outline-none border-0 hover:bg-slate-50 rounded-lg cursor-pointer"
              >
                <option value="">All Types</option>
                <option value="apartment">Apartment</option>
                <option value="villa">Villa</option>
                <option value="plot">Plot</option>
                <option value="commercial">Commercial</option>
              </select>
            </div>
            <div className="md:col-span-4 md:border-l md:border-slate-100">
              <label className="overline block mb-1.5 px-2 md:pl-4">Keyword</label>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                data-testid="hero-search-input"
                placeholder="Project, developer or locality"
                className="w-full bg-white text-slate-900 px-3 md:px-4 py-3 text-sm font-medium focus:outline-none border-0 placeholder:text-slate-400"
              />
            </div>
            <div className="md:col-span-2">
              <button
                type="submit"
                data-testid="hero-search-btn"
                className="w-full bg-[#D4A437] hover:bg-[#B88C2E] text-white font-semibold py-3 rounded-lg transition-all flex items-center justify-center gap-2 shadow-md hover:shadow-lg active:scale-95"
              >
                <Search className="w-4 h-4" strokeWidth={2.5} /> Search
              </button>
            </div>
          </form>

          {/* Quick city chips */}
          <div className="mt-6 flex flex-wrap gap-2 max-w-3xl">
            <span className="text-xs font-semibold text-slate-500 mr-2 self-center">Popular:</span>
            {["Lucknow", "Gurugram", "Mumbai", "Bangalore", "Dubai"].map((c) => (
              <Link
                key={c}
                to={`/city/${c}`}
                data-testid={`hero-quick-${c.toLowerCase()}`}
                className="text-xs font-medium px-3 py-1.5 bg-white border border-slate-200 rounded-full hover:border-[#D4A437] hover:text-[#D4A437] transition-colors"
              >
                {c}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Cities tile grid */}
      <section className="py-20 md:py-24 max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-5">
            <div className="overline mb-3">Cities we operate in</div>
            <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight font-bold">
              Ten cities. <br />One <span className="text-[#D4A437]">trusted</span> consultancy.
            </h2>
          </div>
          <div className="md:col-span-7 text-slate-600 leading-relaxed pt-3">
            Our consultants specialise in markets that matter — from our home-base of Lucknow,
            to Gurugram, Noida, Delhi, Mumbai, Bangalore, Pune and Hyderabad across India,
            and overseas in Dubai and Singapore.
            RERA-verified inventory, transparent pricing and a single point of accountability.
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4">
          {CITIES.map((c, idx) => {
            const stat = stats.find((s) => s.city === c);
            return (
              <Link
                to={`/city/${c}`}
                key={c}
                data-testid={`city-tile-${c}`}
                className="bg-white rounded-xl border border-slate-100 hover:border-[#D4A437]/40 hover:shadow-lg hover:-translate-y-0.5 transition-all p-5 group"
                style={{ animationDelay: `${idx * 60}ms` }}
              >
                <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center mb-3 group-hover:bg-[#D4A437] transition-colors">
                  <Globe2 className="w-5 h-5 text-[#D4A437] group-hover:text-white transition-colors" strokeWidth={2} />
                </div>
                <div className="font-serif text-lg font-bold text-slate-900 group-hover:text-[#D4A437] transition-colors">{c}</div>
                <div className="text-xs text-slate-500 mt-1">
                  {stat ? `${stat.count} project${stat.count > 1 ? "s" : ""}` : "Explore →"}
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-20 md:py-24 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
            <div>
              <div className="overline mb-3">Hand-picked</div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900">Featured Listings</h2>
              <p className="text-slate-600 mt-3 max-w-xl">A rotating shortlist of our most-requested addresses across the OC Infraventures catalog.</p>
            </div>
            <Link
              to="/projects"
              className="hidden md:inline-flex text-sm font-semibold text-[#D4A437] hover:text-[#B88C2E] items-center gap-2"
              data-testid="view-all-projects"
            >
              View all <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <FeaturedCarousel projects={featured} />
        </div>
      </section>

      {/* Hot Deals banner */}
      {hotDeals.length > 0 && (
        <section
          data-testid="home-hot-deals-section"
          className="relative py-20 md:py-24 bg-gradient-to-br from-orange-50 via-white to-amber-50 overflow-hidden"
        >
          <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-orange-200/40 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-amber-200/30 blur-3xl pointer-events-none" />
          <div className="relative max-w-7xl mx-auto px-4 md:px-8">
            <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
              <div>
                <div className="overline mb-3 flex items-center gap-2 text-[#EF4444]">
                  <Flame className="w-3.5 h-3.5" fill="currentColor" /> Limited time · {hotDeals.length} active offers
                </div>
                <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900">
                  Hot Deals <span className="text-[#EF4444]">&amp;</span> Discounted Allocations
                </h2>
                <p className="text-slate-600 mt-3 max-w-xl">
                  Festive offers, pre-launch allocations and developer-direct discounts — vetted, time-bound, RERA-compliant.
                </p>
              </div>
              <Link
                to="/hot-deals"
                data-testid="home-hot-deals-view-all"
                className="inline-flex items-center gap-2 bg-[#EF4444] hover:bg-[#DC2626] text-white font-semibold rounded-lg px-6 py-3 transition-colors shadow-md hover:shadow-lg"
              >
                View all hot deals <ArrowRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {hotDeals.slice(0, 4).map((p) => {
                const discounted = p.discount_pct ? Math.round(p.price_min * (1 - p.discount_pct / 100)) : null;
                return (
                  <Link
                    key={p.id}
                    to={`/projects/${p.id}`}
                    data-testid={`home-hot-deal-${p.id}`}
                    className="group bg-white rounded-2xl border border-slate-100 hover:border-[#EF4444]/40 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.10)] hover:-translate-y-1 transition-all overflow-hidden"
                  >
                    <div className="relative aspect-[16/11] bg-slate-100 overflow-hidden">
                      <img
                        src={p.images?.[0] || "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1000"}
                        alt={p.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      {p.discount_pct && (
                        <div className="absolute top-3 left-3 bg-[#EF4444] text-white px-3 py-1.5 flex items-center gap-1 font-bold text-xs rounded-lg shadow-md">
                          <Flame className="w-3 h-3" strokeWidth={2.5} fill="currentColor" />
                          {p.discount_pct}% OFF
                        </div>
                      )}
                      {p.deal_label && (
                        <div className="absolute bottom-3 left-3 bg-white/95 text-[10px] tracking-wider uppercase font-bold text-slate-700 px-2.5 py-1 rounded-md inline-flex items-center gap-1.5 shadow-sm">
                          <Tag className="w-3 h-3 text-[#EF4444]" /> {p.deal_label}
                        </div>
                      )}
                    </div>
                    <div className="p-5">
                      <div className="text-xs text-[#B88C2E] font-medium mb-1">{p.locality}, {p.city}</div>
                      <h3 className="font-serif text-base font-bold text-slate-900 group-hover:text-[#D4A437] transition-colors line-clamp-1">{p.name}</h3>
                      <div className="mt-4 flex items-baseline gap-2">
                        {discounted ? (
                          <>
                            <div className="font-bold text-xl text-[#D4A437]">{formatPrice(discounted, p.currency)}</div>
                            <div className="text-xs text-slate-400 line-through">{formatPrice(p.price_min, p.currency)}</div>
                          </>
                        ) : (
                          <div className="font-bold text-xl text-[#D4A437]">{formatPrice(p.price_min, p.currency)}</div>
                        )}
                      </div>
                    </div>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Why buy with us — services */}
      <section className="py-20 md:py-24 max-w-7xl mx-auto px-4 md:px-8">
        <div className="overline mb-3">Why buy with us</div>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-12 max-w-3xl">A complete realty partner — from search to keys, and beyond.</h2>

        <div className="grid md:grid-cols-3 lg:grid-cols-5 gap-4">
          {[
            { num: "01", icon: HomeIcon, title: "Real Estate Consulting", body: "One-stop solution for all your realty needs across India and overseas." },
            { num: "02", icon: Briefcase, title: "Home Loan Consultation", body: "Tied up with 24+ banks for the best rates and 72-hour sanctions.", to: "/home-loans" },
            { num: "03", icon: Scale, title: "Legal Consultation", body: "Title search, RERA verification and registration by our in-house panel." },
            { num: "04", icon: Globe2, title: "NRI Services", body: "FEMA, repatriation and end-to-end remote investment for NRIs.", to: "/nri-services" },
            { num: "05", icon: HeartHandshake, title: "After-Sales CRM", body: "Our concierge team continues to assist you long after the keys are handed over." },
          ].map((s) => {
            const Tag = s.to ? Link : "div";
            return (
              <Tag
                key={s.num}
                {...(s.to ? { to: s.to } : {})}
                className="bg-white rounded-2xl border border-slate-100 hover:shadow-lg hover:-translate-y-0.5 transition-all p-6 group"
              >
                <div className="text-xs font-bold text-[#D4A437] mb-3">{s.num}</div>
                <div className="w-11 h-11 rounded-lg bg-amber-50 flex items-center justify-center mb-4 group-hover:bg-[#D4A437] transition-colors">
                  <s.icon className="w-5 h-5 text-[#D4A437] group-hover:text-white transition-colors" strokeWidth={2} />
                </div>
                <h3 className="font-serif text-lg font-bold text-slate-900 mb-2">{s.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{s.body}</p>
              </Tag>
            );
          })}
        </div>
      </section>

      {/* Decision tools */}
      <section className="py-20 md:py-24 bg-white border-y border-slate-100">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="overline mb-3">Decision tools</div>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-12 max-w-3xl">Built to make your decision easier.</h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: TrendingUp, title: "Price Insights", body: "Live 2026 city heatmaps, locality benchmarks and YoY growth across 10 metros.", to: "/insights", testId: "service-insights", color: "from-rose-50 to-pink-50", iconBg: "bg-rose-100", iconColor: "text-rose-600" },
              { icon: Calculator, title: "Property Valuation", body: "Free instant fair-value estimate weighted by city, locality, age, floor and facing.", to: "/valuation", testId: "service-valuation", color: "from-amber-50 to-orange-50", iconBg: "bg-amber-100", iconColor: "text-[#D4A437]" },
              { icon: Compass, title: "Vastu Calculator", body: "Score your home against classical Vastu rules. Room-wise feedback with remedies.", to: "/vastu", testId: "service-vastu", color: "from-indigo-50 to-violet-50", iconBg: "bg-indigo-100", iconColor: "text-indigo-600" },
              { icon: GitCompareArrows, title: "Compare Projects", body: "Pit up to three projects side-by-side across price, amenities and locality.", to: "/compare", testId: "service-compare", color: "from-blue-50 to-indigo-50", iconBg: "bg-blue-100", iconColor: "text-blue-600" },
              { icon: Calculator, title: "EMI Calculator", body: "Run scenarios with sliders. See your true cost of ownership year by year.", to: "/emi-calculator", testId: "service-emi", color: "from-yellow-50 to-amber-50", iconBg: "bg-amber-100", iconColor: "text-[#D4A437]" },
              { icon: Building, title: "Post Property", body: "List your residence and reach 12,000+ verified buyers including the diaspora.", to: "/post-property", testId: "service-post", color: "from-emerald-50 to-teal-50", iconBg: "bg-emerald-100", iconColor: "text-emerald-600" },
            ].map((s) => (
              <Link
                key={s.title}
                to={s.to}
                data-testid={s.testId}
                className={`bg-gradient-to-br ${s.color} rounded-2xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all group border border-white`}
              >
                <div className={`w-12 h-12 rounded-xl ${s.iconBg} flex items-center justify-center mb-5`}>
                  <s.icon className={`w-6 h-6 ${s.iconColor}`} strokeWidth={2} />
                </div>
                <h3 className="font-serif text-2xl font-bold text-slate-900 mb-3 group-hover:text-[#D4A437] transition-colors">{s.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{s.body}</p>
                <div className="mt-6 text-[#D4A437] text-sm font-semibold inline-flex items-center gap-1.5">Get Started <ArrowRight className="w-4 h-4" /></div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="py-20 md:py-24 max-w-7xl mx-auto px-4 md:px-8">
        <div className="grid md:grid-cols-4 gap-6">
          {[
            { k: "10+", v: "Years of Trust" },
            { k: "10", v: "Cities Served" },
            { k: "₹4,800 Cr+", v: "Closed Last Year" },
            { k: "98%", v: "Client Satisfaction" },
          ].map((s) => (
            <div key={s.v} className="bg-white rounded-2xl border border-slate-100 p-8 text-center hover:shadow-lg transition-shadow">
              <div className="font-serif text-4xl md:text-5xl text-[#D4A437] font-bold">{s.k}</div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mt-3 font-semibold">{s.v}</div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
